import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { plainToClass } from 'class-transformer';
import { isEmpty } from 'lodash';
import { BehaviorSubject, Observable, of, Subject, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { UserProfileService } from 'src/app/common/service/user-profile.service';
import { RestService } from 'src/app/shared/service/rest.service';
import { TenantInfo } from '../model/tenant-info';
import { SakpModuleUtils } from '../sakp-module-utils';
import { SessionStorageService } from '../../common/service/session-storage.service';

const STORAGE = 'sakp-users';

@Injectable()
export class UserService extends RestService {

    static readonly SOURCE = 'USERS_SOURCE';
    private idKeycloakUsers: Map<string, UserSakpInfo> = new Map();
    private byTenantUpdate = new BehaviorSubject(null);
    private obs: Observable<boolean>;
    private stopUpdateSubject$ = new Subject();

    constructor(http: HttpClient, private userProfileService: UserProfileService, private sessionStorageService: SessionStorageService) {
      super(http);
      this.serverUrl = SakpModuleUtils.getSakpServerUrl();
      const allFromStorage =
        this.sessionStorageService.getItemArray<UserSakpInfo>(
          STORAGE,
          UserSakpInfo,
        );
      if (allFromStorage != null) {
        allFromStorage.forEach((u) => this.idKeycloakUsers.set(u.id, u));
      }

      this.obs = this.byTenantUpdate.asObservable().pipe(
        filter((tenant): tenant is TenantInfo => tenant !== null),
        tap(() => {
            this.idKeycloakUsers.clear();
            this.sessionStorageService.setItem(STORAGE, []);
        }),
        switchMap((tenant) =>
          timer(0, 2 * 60 * 1000).pipe(
            switchMap(() =>
              this.getAllUsersInfos(tenant.id.toString()).pipe(
                distinctUntilChanged(
                    (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
                ),
                tap((all) => {
                    this.sessionStorageService.setItem(STORAGE, all);
                    this.idKeycloakUsers.clear();
                    all.forEach((u) => this.idKeycloakUsers.set(u.id, u));
                }
                ),
                map(() => true),
                catchError(err => {
                    err.source = UserService.SOURCE;
                    console.error(err);
                    return of(false);
                }))
            )
          ),
        ),
        shareReplay(1),
        takeUntil(this.stopUpdateSubject$),
      );
    }

    stopUpdates(): void {
        this.stopUpdateSubject$.next();
    }


    getByIdKeycloak(idKeycloak: string): UserSakpInfo | null {
        return this.idKeycloakUsers.get(idKeycloak) || null;
    }

    initiateWithUpdates(tenantInfo: TenantInfo): Observable<boolean> {
        this.byTenantUpdate.next(tenantInfo);
        return this.obs;
    }

    selectByTenant(tenantInfo: TenantInfo): Observable<boolean> {
        return this.initiateWithUpdates(tenantInfo);
    }

    getUpdateObs(): Observable<boolean> {
        return this.obs;
    }

    // used for initialization
    private getAllUsersInfos(tenantId: string): Observable<UserSakpInfo[]> {
        return this.doGet<UserSakpInfo[]>('api/v1/users/all', null, { ['X-Tenant-ID']: tenantId }).pipe(
            map(us => plainToClass(UserSakpInfo, us))
        );
    }

    getAll(): UserSakpInfo[] {
        return [...this.idKeycloakUsers.values()];
    }

    getMyInfo(): UserSakpInfo | null {
        return this.getByIdKeycloak(this.userProfileService.getUserProfile().idKeycloak);
    }

}

export class UserSakpInfo {
    // info
    readonly id: string;
    username: string;
    firstName: string;
    lastName: string;
    email: string;
    // privilages
    canApproveDrafts: boolean;
    isContractor: boolean;
    canViewDrafts: boolean;
    canCommentOnDisputes: boolean;
    canApproveDisputes: boolean;
    canClaim: boolean;
    canDraft: boolean;
    isMunicipality: boolean;
    isAdmin: boolean;
    canViewFleet: boolean;
    canManageFleetVehicles: boolean;
    canManageFleetRequirements: boolean;

    getUserLabel(): string {
        return !isEmpty(this.firstName) && !isEmpty(this.lastName) ? `${this.firstName} ${this.lastName}` : this.username;
    }

}

